import moment from 'moment';
import { isInvalidOccupancy, sanitizeOccupancy } from 'Shared/utils/OccupancyHelper';
import isDateInvalid from 'Shared/utils/DatesHelper';

const processRequestStart = state => (
    {
        ...state,
        isLoading: true,
    }
);

const processRequestFailure = (state, action) => (
    {
        ...state,
        isLoading : false,
        data      : { ...state.data, errorInformation: action.payload },
        error     : true,
    }
);

const processRequestSuccess = (state, action) => {
    // When the initial data is fetched we search as an initial state with the search params
    const {
        checkIn, checkOut, defaultOccupancy, language, currency, minOccupancy, maxOccupancy,
        childrenEnabled, minChildAge, maxChildAge,
    } = action.payload.settings;

    const checkingParams = { childrenEnabled, minOccupancy, maxOccupancy, minChildAge, maxChildAge };

    const finalDefaultOccupancy = defaultOccupancy?.length ? defaultOccupancy : [ defaultOccupancy ];

    const processOccupancy = () => {
        const finalOccupancy = Array.isArray(state.data?.occupancy) ? state.data.occupancy : [ defaultOccupancy ];

        if (isInvalidOccupancy(finalOccupancy, checkingParams)) {
            return sanitizeOccupancy(finalOccupancy, checkingParams);
        }
        return finalOccupancy;
    };

    const processDefaultOccupancy = () => {
        const finalOccupancy =  Array.isArray(state.data?.defaultOccupancy) ? state.data.defaultOccupancy : [ defaultOccupancy ];
        if (isInvalidOccupancy(finalOccupancy, checkingParams)) {
            return sanitizeOccupancy(finalOccupancy, checkingParams);
        }
        return finalOccupancy;
    };

    const newLanguage = state.data?.language !== language ? language : state.data.language;

    return {
        isLoading : false,
        data      : {
            ...state.data,
            checkIn          : checkIn || state.data?.checkIn,
            checkOut         : checkOut || state.data?.checkOut,
            language         : newLanguage,
            currency         : state.data?.currency || currency,
            occupancy        : processOccupancy(),
            defaultOccupancy : processDefaultOccupancy(),
        },
        error: false,
    };
};

const processChangeCurrency = (state, action) => (
    {
        ...state,
        data: {
            ...state.data,
            currency: action.payload,
        },
    }
);

const processChangePromoCode = (state, action) => (
    {
        ...state,
        data: {
            ...state.data,
            clientPromoCode: action.payload,
        },
    }
);

const processChangeLanguage = (state, action) => (
    {
        ...state,
        data: {
            ...state.data,
            language: action.payload,
        },
    }
);

const processChangeUserAgent = (state, action) => (
    {
        ...state,
        data: {
            ...state.data,
            userAgent: action.payload,
        },
    }
);

const processChangeDates = (state, action) => {
    const { checkIn, checkOut, los, previousCheckIn, previousCheckOut } = action.payload;

    let newCheckIn; let newCheckout;

    const isInvalidDate = checkIn ? isDateInvalid(checkIn, checkOut) : isDateInvalid(previousCheckIn, checkOut);
    if (isInvalidDate) {
        if (checkIn && los) {
            newCheckIn = checkIn;
            newCheckout = moment(checkIn, 'YYYY-MM-DD').add(los, 'd').format('YYYY-MM-DD');
        } else {
            newCheckIn = previousCheckIn;
            newCheckout = previousCheckOut;
        }
    } else {
        newCheckIn = checkIn;
        newCheckout = checkOut;
    }

    return (
        {
            ...state,
            data: {
                ...state.data,
                checkIn  : newCheckIn,
                checkOut : newCheckout,
            },
        }
    );
};

const processChangeRoomQuantity = (state, action) => {
    const { numberOfRooms, defaultOccupancy } = action.payload;
    const diff = Math.abs(state.data.occupancy.length - numberOfRooms);
    let newOccupancy = [ ...state.data.occupancy ];

    if (numberOfRooms > state.data.occupancy.length) {
        const occupancy = [];
        for (let i = 1; i <= diff; i++) {
            occupancy.push(defaultOccupancy[0]);
        }

        newOccupancy = [ ...state.data.occupancy, ...occupancy ];
    } else if (numberOfRooms < state.data.occupancy.length) {
        newOccupancy = [ ...state.data.occupancy.slice(0, diff * -1) ];
    }
    return {
        ...state,
        data: {
            ...state.data,
            occupancy: newOccupancy,
        },
    };
};

const processRemoveRoom = (state, action) => {
    const { index } = action.payload;
    const tempOccupancy = [ ...state.data.occupancy ];
    tempOccupancy.splice(index, 1);

    return {
        ...state,
        data: {
            ...state.data,
            occupancy: tempOccupancy,
        },
    };
};

const processChangeAdults = (state, action) => {
    const { numberOfAdults, adultsRoomIndex } = action.payload;
    return {
        ...state,
        data: {
            ...state.data,
            occupancy: state.data.occupancy.map((room, index) => {
                if (index !== adultsRoomIndex) {
                    return room;
                }
                return { ...room, adults: numberOfAdults };
            }),
        },
    };
};

const processChangeChildren = (state, action) => {
    const { numberOfChildren, childrenRoomIndex, childAge } = action.payload;
    return {
        ...state,
        data: {
            ...state.data,
            occupancy: state.data.occupancy.map((room, index) => {
                // if is no the room we indicate, no change is made
                if (index !== childrenRoomIndex) {
                    return room;
                }
                // The new children's number is less than the stored one, so we are deleting
                if (room.children.length > numberOfChildren) {
                    return { ...room, children: room.children.slice(0, -1) };
                }
                // If we reach here, we are editing the age of a child
                return { ...room, children: [ ...room.children, childAge ] };
            }),
        },
    };
};

const processChangeChildrenAge = (state, action) => {
    const { childrenAgeRoomIndex, childIndex, childrenAge } = action.payload;
    return {
        ...state,
        data: {
            ...state.data,
            occupancy: state.data.occupancy.map((room, index) => {
                if (index !== childrenAgeRoomIndex) {
                    return room;
                }
                return {
                    ...room,
                    children: room.children.map((children, i) => {
                        if (i !== childIndex) {
                            return children;
                        }
                        return childrenAge;
                    }),
                };
            }),
        },
    };
};

const processReplaceCurrencyCode = (state, action) => (
    {
        ...state,
        data: {
            ...state.data,
            currency: {
                ...state.data.currency,
                code: action.payload,
            },
        },
    }
);

const processUpdateCoupon = (state, action) => (
    {
        ...state,
        data: {
            ...state.data,
            coupon: action.payload,
        },
    }
);

const initialState = {
    isLoading : true,
    data      : null,
    error     : false,
};

const search = (state = initialState, action = { type: null }) => {
    switch (action.type) {
        case 'DATA_REQUEST_START':

            return processRequestStart(state);

        case 'DATA_REQUEST_FAILURE':

            return processRequestFailure(state, action);

        case 'DATA_REQUEST_SUCCESS':

            return processRequestSuccess(state, action);

        case 'CHANGE_CURRENCY':

            return processChangeCurrency(state, action);

        case 'SET_CURRENCY_TYPE':
            return processReplaceCurrencyCode(state, action);

        case 'CHANGE_PROMO_CODE':

            return processChangePromoCode(state, action);

        case 'CHANGE_LANGUAGE':

            return processChangeLanguage(state, action);

        case 'CHANGE_DATES':

            return processChangeDates(state, action);

        case 'CHANGE_ROOMS_QUANTITY':

            return processChangeRoomQuantity(state, action);

        case 'REMOVE_ROOM':

            return processRemoveRoom(state, action);

        case 'CHANGE_ADULTS':

            return processChangeAdults(state, action);

        case 'CHANGE_CHILDREN':

            return processChangeChildren(state, action);

        case 'CHANGE_CHILDREN_AGE':

            return processChangeChildrenAge(state, action);
        case 'CHANGE_USER_AGENT':
            return processChangeUserAgent(state, action);
        // Warning is one of: shouldUpgradeTier, shouldHideHotel
        case 'SET_ROOMS_WARNING':
        case 'SET_SEARCH_KEY':
            return {
                ...state,
                data: {
                    ...state.data,
                    [action.payload.key]: action.payload.value,
                },
            };
        case 'UPDATE_COUPON':
            return processUpdateCoupon(state, action);
        default:
            return state;
    }
};

export default search;
